import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-user-list.css?inline';
import { withCsrf } from '../../lib/csrf.js';
import '../pk-data-table/pk-data-table.js';

// ---------------------------------------------------------------------------
// Role mapping
// ---------------------------------------------------------------------------

export const ROLE_LABELS: Record<number, string> = {
    0: 'Reader',
    1: 'Auditor',
    2: 'Member',
    3: 'Operator',
    4: 'Owner',
};

export const ROLE_CLASSES: Record<number, string> = {
    0: 'role-reader',
    1: 'role-auditor',
    2: 'role-member',
    3: 'role-operator',
    4: 'role-owner',
};

/** Convert a role byte (0–4) to its label string. */
export function roleLabel(byte: number): string {
    return ROLE_LABELS[byte] ?? `Role ${byte}`;
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

// pk-data-table is generic; we'll override cell rendering via a thin wrapper
// that pre-processes the items before passing them to the table.

interface RawUser {
    email:         string;
    role:          number;
    status:        string;
    emailVerified: boolean;
    totpEnabled:   boolean;
    createdAt:     string;
}

interface MappedUser {
    email:         string;
    roleLabel:     string;
    status:        string;
    emailVerified: string;
    totpEnabled:   string;
    createdAt:     string;
}

function mapUser(u: RawUser): MappedUser {
    return {
        email:         u.email,
        roleLabel:     roleLabel(u.role),
        status:        u.status,
        emailVerified: u.emailVerified ? '✓' : '—',
        totpEnabled:   u.totpEnabled   ? '✓' : '—',
        createdAt:     u.createdAt,
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<pk-user-list>` — team roster table for a tenant.
 *
 * Attributes:
 *   `tenant-id`  (REQUIRED)
 *   `api-base`   (default `/portal/api/al`)
 *
 * Events:
 *   `pk-row-click` — from inner pk-data-table; detail = `{ row }`.
 */
export class PkUserList extends HTMLElement {
    static readonly tagName = 'pk-user-list';

    static get observedAttributes(): string[] {
        return ['tenant-id', 'api-base'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    get tenantId(): string { return this.getAttribute('tenant-id') ?? ''; }
    get apiBase(): string  { return this.getAttribute('api-base') ?? '/portal/api/al'; }

    connectedCallback(): void {
        this._doRender();
        this._loadUsers();
    }

    attributeChangedCallback(_name: string, oldVal: string | null, newVal: string | null): void {
        if (oldVal !== newVal && this.isConnected) this._loadUsers();
    }

    private async _loadUsers(): Promise<void> {
        if (!this.tenantId) return;

        // We intercept the fetch here so we can map the role byte to a label
        // before pk-data-table sees the items. We do this by wrapping fetch.
        const url = `${this.apiBase}/tenants/${encodeURIComponent(this.tenantId)}/users`;

        try {
            const resp = await fetch(url, withCsrf());
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json() as { items?: RawUser[] } | RawUser[];
            const rawItems = Array.isArray(data) ? data : (data.items ?? []);
            const mapped = rawItems.map(mapUser);

            const table = this._root.querySelector('pk-data-table');
            if (!table) return;

            // Bypass setConfig — it would trigger a second fetch and race against
            // our injection. Reach into the table's private fields directly to set
            // config + rows + load state, then render once.
            const t = table as unknown as {
                _config: { src: string; columns: unknown[] };
                _rows: unknown[];
                _total: number;
                _loadState: string;
                _doRender(): void;
            };
            t._config = {
                src: url,
                columns: [
                    { key: 'email',         label: 'Email'          },
                    { key: 'roleLabel',     label: 'Role'           },
                    { key: 'status',        label: 'Status'         },
                    { key: 'emailVerified', label: 'Email verified' },
                    { key: 'totpEnabled',   label: 'TOTP'           },
                    { key: 'createdAt',     label: 'Created',       type: 'date' },
                ],
            };
            t._rows      = mapped;
            t._total     = mapped.length;
            t._loadState = 'ok';
            t._doRender();
        } catch {
            // Let pk-data-table's own error state surface via a real fetch.
            const table = this._root.querySelector('pk-data-table');
            if (table) {
                table.setConfig({
                    src: url,
                    columns: [
                        { key: 'email',     label: 'Email'  },
                        { key: 'roleLabel', label: 'Role'   },
                        { key: 'status',    label: 'Status' },
                    ],
                });
            }
        }
    }

    private template(): TemplateResult {
        return html`
            <style>${styles}</style>
            <div class="panel">
                <div class="header">
                    <h2 class="header-title">Team members</h2>
                </div>
                <div class="table-host">
                    <pk-data-table>
                        <span slot="empty-cta">No team members yet — invite one.</span>
                    </pk-data-table>
                </div>
            </div>
        `;
    }

    private _doRender(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkUserList.tagName)) {
    customElements.define(PkUserList.tagName, PkUserList);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-user-list': PkUserList;
    }
}
